"use client";

import { COOKIE_ANALYTICS_WITHDRAWING_EVENT, COOKIE_CONSENT_UPDATED_EVENT, getCookieConsent } from "@/lib/consent";
import { ANALYTICS_URL_SANITIZED_EVENT, sanitizeUrlForAnalytics } from "@/lib/privacy/urlSanitization";
import { CLARITY_PUBLIC_PATHS } from "@/lib/analytics/clarityPublicPaths";

// Pin the reviewed official runtime. The hosted project bootstrap registers a
// deferred advertising pixel outside Clarity's stop/start lifecycle.
export const CLARITY_RUNTIME_URL = "/vendor/clarity/0.8.69-consent.1.js";
const PROJECT_ID = "wig27tgela";
const UPLOAD_URL = "https://n.clarity.ms/collect";
const SCRIPT_ID = "microsoft-clarity";

declare global {
  interface Window { __saClarityCanStart?: () => boolean; }
}

type Metadata = { projectId: string; userId: string; sessionId: string; pageNum: number };

function clearClarityStorage() {
  const host = window.location.hostname;
  const domains = ["", ...host.split(".").map((_, i, parts) => parts.slice(i).join("."))];
  for (const name of ["_clck", "_clsk"]) {
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain ? `; Domain=${domain}` : ""}`;
    }
  }
  try { window.sessionStorage.removeItem("_cltk"); } catch { /* Storage can be blocked. */ }
}

/** One document owns the runtime and every upload; no browser APIs are patched. */
export function createClarityLifecycle() {
  let ready = false;
  let running = false;
  let disposed = false;
  let generation = 0;
  let session: Metadata | null = null;
  let marketing: boolean | null = null;
  let script: HTMLScriptElement | null = null;
  let navigationRecovery: number | null = null;
  const requests = new Set<AbortController>();
  const pendingEvents: string[] = [];

  function stop() {
    if (navigationRecovery !== null) window.clearInterval(navigationRecovery);
    navigationRecovery = null;
    generation++;
    // Close admission BEFORE stop(), which otherwise flushes buffered data.
    running = false;
    session = null;
    marketing = null;
    pendingEvents.length = 0;
    for (const request of requests) request.abort();
    requests.clear();
    if (ready) window.clarity?.("stop");
    if (window.clarity?.q) window.clarity.q.length = 0;
    clearClarityStorage();
    // Do not queue consentv2 denied: Clarity restarts limited tracking on denial.
  }

  function canRecordPage() {
    if (!CLARITY_PUBLIC_PATHS.has(window.location.pathname)) return false;
    // Session replay owns URL collection too. Do not start it on a URL that the
    // application sanitizer would redact; leave functional browser URLs intact.
    const href = window.location.href;
    const options = { removeClickIds: !getCookieConsent().marketing };
    // The SDK also records document.referrer. A private previous page remains a
    // property of this document across SPA navigation, so fail closed throughout.
    const referrer = document.referrer;
    if (referrer) {
      const previous = new URL(referrer);
      if (previous.origin === window.location.origin && !CLARITY_PUBLIC_PATHS.has(previous.pathname)) return false;
      if (sanitizeUrlForAnalytics(referrer, options) !== referrer) return false;
    }
    return sanitizeUrlForAnalytics(href, options) === href;
  }
  window.__saClarityCanStart = () => !disposed && running && getCookieConsent().analytics && canRecordPage();

  function upload(payload: string) {
    if (disposed || !running || !getCookieConsent().analytics || !canRecordPage()) return;
    // Navigation changes the SDK metadata; read it synchronously for this batch.
    session = null;
    window.clarity?.("metadata", (value: Metadata) => { session = value; }, false);
    if (!session) return;
    const currentSession = session as Metadata;
    // A compression promise from the previous session can finish after regrant.
    // Never admit its payload into the new consent generation.
    try {
      const envelope: unknown = (JSON.parse(payload) as { e?: unknown }).e;
      if (!Array.isArray(envelope) || envelope[4] !== currentSession.projectId ||
          envelope[5] !== currentSession.userId || envelope[6] !== currentSession.sessionId ||
          envelope[7] !== currentSession.pageNum) return;
    } catch { return; }
    const currentGeneration = generation;
    const request = new AbortController();
    requests.add(request);
    // The SDK's supported upload callback gives the site the final consent gate.
    // Send its original JSON payload; do not retain it or retry across decisions.
    void fetch(UPLOAD_URL, {
      method: "POST", body: payload, credentials: "include", signal: request.signal,
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
    }).then(async (response) => {
      const ended = response.ok && (await response.text()).split("\n").some(line => line.split(" ")[0] === "END");
      if ((!response.ok || ended) && generation === currentGeneration && running) {
        // A failed replay must not continue accumulating data for a later grant.
        stop();
      }
    }).catch(() => {
      if (!request.signal.aborted && generation === currentGeneration && running) stop();
    }).finally(() => requests.delete(request));
  }

  function sync() {
    if (disposed) return;
    const consent = getCookieConsent();
    if (!consent.analytics || !canRecordPage()) {
      stop();
      if (consent.analytics && ready) {
        // A native hash change can leave Next's canonical URL unchanged. A
        // subsequent pushState back to that URL then emits neither a context
        // update nor hashchange. Observe recovery only while recording is OFF;
        // stopping and upload admission never rely on a polling timer.
        const excludedUrl = window.location.href;
        navigationRecovery = window.setInterval(() => {
          if (window.location.href !== excludedUrl && canRecordPage()) sync();
        }, 100);
      }
      return;
    }
    if (navigationRecovery !== null) window.clearInterval(navigationRecovery);
    navigationRecovery = null;
    if (!script) {
      // Keep only name-only events in our queue. The SDK executes its own queue
      // before onload/start, so handing pending events to that queue loses them.
      window.clarity = (command: string, ...args: unknown[]) => {
        if (command === "event" && typeof args[0] === "string" && pendingEvents.length < 100 &&
            !disposed && getCookieConsent().analytics) pendingEvents.push(args[0]);
      };
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.async = true;
      script.nonce = document.querySelector<HTMLScriptElement>("script[nonce]")?.nonce ?? "";
      script.src = CLARITY_RUNTIME_URL;
      script.onload = () => { ready = true; sync(); };
      script.onerror = () => { script?.remove(); script = null; };
      document.head.appendChild(script);
    }
    if (!ready) return;
    if (!running) {
      if (window.clarity?.q) window.clarity.q.length = 0;
      generation++;
      running = true;
      window.clarity?.("start", {
        // Start is already gated by an explicit analytics grant. track:true
        // restores cookies on same-category regrant; no advertising IDs are copied.
        projectId: PROJECT_ID, upload, track: true, content: true,
        cookies: [], dob: 2441,
      });
    }
    if (marketing !== consent.marketing) {
      window.clarity?.("consentv2", {
        analytics_Storage: "granted",
        ad_Storage: consent.marketing ? "granted" : "denied",
      });
      marketing = consent.marketing;
    }
    for (const event of pendingEvents.splice(0)) window.clarity?.("event", event);
  }

  // Capture phase runs before Google's bubble-phase storage consent listeners.
  window.addEventListener(ANALYTICS_URL_SANITIZED_EVENT, sync, true);
  window.addEventListener(COOKIE_ANALYTICS_WITHDRAWING_EVENT, stop, true);
  window.addEventListener(COOKIE_CONSENT_UPDATED_EVENT, sync, true);
  window.addEventListener("storage", sync, true);
  window.addEventListener("hashchange", sync, true);
  window.addEventListener("popstate", sync, true);
  sync();
  function dispose() {
    disposed = true;
    window.removeEventListener(ANALYTICS_URL_SANITIZED_EVENT, sync, true);
    window.removeEventListener(COOKIE_ANALYTICS_WITHDRAWING_EVENT, stop, true);
    window.removeEventListener(COOKIE_CONSENT_UPDATED_EVENT, sync, true);
    window.removeEventListener("storage", sync, true);
    window.removeEventListener("hashchange", sync, true);
    window.removeEventListener("popstate", sync, true);
    stop();
    if (script) {
      script.onload = null;
      script.onerror = null;
      script.remove();
    }
  }
  return { sync, dispose };
}
